import { useParams } from 'react-router-dom';
import { SectionBlock, SectionBlockSeparator, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { moneyAccountsMessages } from 'src/features/moneyAccounts/utils/messages';
import { accountingHeadersMessages } from '../accountingHeaders/utils/messages';
import { accountingRangesMessages } from '../accountingRanges/utils/messages';
import { billingMessages } from '../billing/utils/messages';
import { servicePlansMessages } from '../servicePlans/utils/messages';
import { cn } from 'src/utils';
import { customersMessages } from 'src/features/customers/utils/messages';
import { paymentMethodsMessages } from '../paymentMethods/utils/messages';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { taxReasonsMessages } from '../taxReasons/utils/messages';
import { servicePlanCategoriesMessages } from 'src/features/servicePlanCategory/utils/messages';
import { referenceRatesMessages } from 'src/features/referenceRates/utils/messages';
import { billableMetricsMessages } from 'src/features/billableMetrics/utils/messages';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { useHasVerticalScroll } from 'src/features/navigation/hooks/useHasVerticalScroll';
import { unitsMessages } from 'src/features/units/utils/messages';
import { chargesMessages } from 'src/features/charges/utils/messages';
import { billingRunsMessages } from 'src/features/billingRuns/utils/messages';
import { billingAttemptsMessages } from 'src/features/billingAttempts/utils/messages';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { bankAccountMessages } from 'src/features/billingEntityBankAccounts/utils/messages';
import logo from 'theme-logo';
import { generalMessages } from 'src/utils/messages';
import { webhookEndpointsMessages } from 'src/features/webhook-endpoints/utils/messages';
import { webhookEventsMessages } from 'src/features/webhook-events/utils/messages';
import { PERMISSION } from 'src/features/auth/types';
import useHasPermissions from 'src/utils/useHasPermissions';
import SidebarButton from 'src/features/navigation/components/SidebarButton';
import { rolesMessages } from 'src/features/roles/utils/messages';
import { profileMessages } from 'src/features/profile/utils/messages';

const Sidebar = () => {
    const isMobile = useMediaQuery('(max-width: 639px)');
    const { formatMessage } = useIntl();
    const {
        entityId = '',
        rangeId = '',
        servicePlanId = '',
        servicePlanItemId = '',
        customerId = '',
        subscriptionId = '',
        subscriptionItemId = '',
        paymentId = '',
        financialDocumentId = '',
        discountPolicyId = '',
        voucherTypeId = '',
        voucherId = '',
        discountId = '',
        roleId = '',
        referenceRateId = '',
        billingRunId = '',
    } = useParams();

    const { ref, hasScroll } = useHasVerticalScroll<HTMLDivElement>();

    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);
    const permissionSubscriptions = useHasPermissions([PERMISSION.ACCESS_SUBSCRIPTIONS]);
    const permissionTaxReasons = useHasPermissions([PERMISSION.ACCESS_TAX_REASONS]);
    const permissionServicePlans = useHasPermissions([PERMISSION.ACCESS_SERVICE_PLANS]);
    const permissionDiscounts = useHasPermissions([PERMISSION.ACCESS_DISCOUNTS]);
    const permissionVouchers = useHasPermissions([PERMISSION.ACCESS_VOUCHERS]);
    const permissionCharges = useHasPermissions([PERMISSION.ACCESS_CHARGES]);
    const permissionFinancialDocuments = useHasPermissions([PERMISSION.ACCESS_FINANCIAL_DOCUMENTS]);
    const permissionBillingEntities = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);
    const permissionAccountingHeadings = useHasPermissions([PERMISSION.ACCESS_ACCOUNTING_HEADINGS]);
    const permissionAccountingRanges = useHasPermissions([PERMISSION.ACCESS_ACCOUNTING_RANGES]);
    const permissionUnits = useHasPermissions([PERMISSION.ACCESS_UNITS]);
    const permissionMoneyAccounts = useHasPermissions([PERMISSION.ACCESS_MONEY_ACCOUNTS]);
    const permissionAccessRoles = useHasPermissions([PERMISSION.ACCESS_ROLES]);
    const permissionAccessUsers = useHasPermissions([PERMISSION.ACCESS_USERS]);

    const showSubscriptionsSection = useHasPermissions(
        [PERMISSION.ACCESS_SUBSCRIPTIONS, PERMISSION.ACCESS_SERVICE_PLANS],
        true,
    );
    const showDiscountsSection = useHasPermissions([PERMISSION.ACCESS_DISCOUNTS, PERMISSION.ACCESS_VOUCHERS], true);
    const showFinancialsSection = useHasPermissions(
        [PERMISSION.ACCESS_CHARGES, PERMISSION.ACCESS_PAYMENTS, PERMISSION.ACCESS_FINANCIAL_DOCUMENTS],
        true,
    );
    const showBillingEntitiesSection = useHasPermissions(
        [
            PERMISSION.ACCESS_BILLING_ENTITIES,
            PERMISSION.ACCESS_MONEY_ACCOUNTS,
            PERMISSION.ACCESS_ACCOUNTING_HEADINGS,
            PERMISSION.ACCESS_ACCOUNTING_RANGES,
            PERMISSION.ACCESS_UNITS,
            PERMISSION.ACCESS_TAX_REASONS,
        ],
        true,
    );
    const showUsersSections = useHasPermissions([PERMISSION.ACCESS_USERS, PERMISSION.ACCESS_ROLES], true);

    return (
        <div
            ref={ref}
            className={cn(
                'sticky top-0 -mb-4 border-r border-gray-200 pr-8',
                !isMobile && 'h-[calc(100vh-2rem)] overflow-x-hidden overflow-y-auto pr-4',
                hasScroll ? 'min-w-[206px]' : 'min-w-[196px]',
            )}
        >
            <div className={cn('sticky top-0 z-50 flex w-full items-center bg-white py-1', isMobile && 'px-4')}>
                <img
                    src={logo}
                    alt={formatMessage(generalMessages.logo)}
                    className="h-10 w-auto max-w-full object-contain object-left"
                />
            </div>

            <SectionBlock className={cn('flex flex-col sm:gap-3', isMobile && 'ml-4')}>
                {permissionAccessCustomers && (
                    <SectionBlockTitle className="text-base">
                        {formatMessage(customersMessages.customers)}
                    </SectionBlockTitle>
                )}
                <SidebarButton
                    urlLink={`/customers`}
                    title={formatMessage(customersMessages.customers)}
                    additionalActivePaths={[`/customers/${customerId}`]}
                    hasPermission={permissionAccessCustomers}
                />
                <SidebarButton
                    urlLink={`/payment-methods`}
                    title={formatMessage(paymentMethodsMessages.paymentMethods)}
                    hasPermission={permissionAccessCustomers}
                />

                {permissionAccessCustomers && <SectionBlockSeparator className="my-2" />}

                {showUsersSections && (
                    <SectionBlockTitle className="text-base">{formatMessage(rolesMessages.users)}</SectionBlockTitle>
                )}
                <SidebarButton
                    urlLink={`/users`}
                    title={formatMessage(rolesMessages.users)}
                    hasPermission={permissionAccessUsers}
                />
                <SidebarButton
                    urlLink={`/roles`}
                    title={formatMessage(rolesMessages.roles)}
                    additionalActivePaths={[`/roles/${roleId}`]}
                    hasPermission={permissionAccessRoles}
                />

                {showUsersSections && <SectionBlockSeparator className="my-2" />}
                {showSubscriptionsSection && (
                    <SectionBlockTitle className="text-base">
                        {formatMessage(subscriptionsMessages.subscriptions)}
                    </SectionBlockTitle>
                )}
                <SidebarButton
                    urlLink={`/subscriptions`}
                    title={formatMessage(subscriptionsMessages.subscriptions)}
                    additionalActivePaths={[
                        `/subscriptions/${subscriptionId}`,
                        `/subscriptions/${subscriptionId}/subscription-item/${subscriptionItemId}`,
                    ]}
                    hasPermission={permissionSubscriptions}
                />
                <SidebarButton
                    urlLink={`/service-plan-categories`}
                    title={formatMessage(servicePlanCategoriesMessages.servicePlanCategories)}
                    hasPermission={permissionServicePlans}
                />
                <SidebarButton
                    urlLink={`/service-plans`}
                    title={formatMessage(servicePlansMessages.servicePlans)}
                    additionalActivePaths={[
                        `/service-plans/${servicePlanId}`,
                        `/service-plan-item/${servicePlanItemId}`,
                    ]}
                    hasPermission={permissionServicePlans}
                />
                <SidebarButton
                    urlLink={`/reference-rates`}
                    title={formatMessage(referenceRatesMessages.referenceRates)}
                    additionalActivePaths={[`/reference-rates/${referenceRateId}`]}
                    hasPermission={permissionServicePlans}
                />
                <SidebarButton
                    urlLink={`/billable-metrics`}
                    title={formatMessage(billableMetricsMessages.billableMetrics)}
                    hasPermission={permissionServicePlans}
                />

                {showSubscriptionsSection && <SectionBlockSeparator className="my-2" />}

                {showDiscountsSection && (
                    <SectionBlockTitle className="text-base">
                        {formatMessage(discountPoliciesMessages.discounts)}
                    </SectionBlockTitle>
                )}
                <SidebarButton
                    urlLink={`/discount-policies`}
                    title={formatMessage(discountPoliciesMessages.discountPolicies)}
                    additionalActivePaths={[`/discount-policies/${discountPolicyId}`]}
                    hasPermission={permissionDiscounts}
                />
                <SidebarButton
                    urlLink={`/discounts`}
                    title={formatMessage(discountPoliciesMessages.discounts)}
                    additionalActivePaths={[`/discounts/${discountId}`]}
                    hasPermission={permissionDiscounts}
                />
                <SidebarButton
                    urlLink={`/vouchers`}
                    title={formatMessage(voucherTypesMessages.vouchers)}
                    additionalActivePaths={[`/vouchers/${voucherId}`]}
                    hasPermission={permissionVouchers}
                />
                <SidebarButton
                    urlLink={`/voucher-types`}
                    title={formatMessage(voucherTypesMessages.voucherTypes)}
                    additionalActivePaths={[`/voucher-types/${voucherTypeId}`]}
                    hasPermission={permissionVouchers}
                />

                {showDiscountsSection && <SectionBlockSeparator className="my-2" />}

                {showFinancialsSection && (
                    <SectionBlockTitle className="text-base">
                        {formatMessage(paymentsMessages.financials)}
                    </SectionBlockTitle>
                )}
                <SidebarButton
                    urlLink={`/charges`}
                    title={formatMessage(chargesMessages.charges)}
                    hasPermission={permissionCharges}
                />
                <SidebarButton
                    urlLink={`/payments`}
                    title={formatMessage(paymentsMessages.payments)}
                    additionalActivePaths={[`/payments/${paymentId}`]}
                    hasPermission={permissionPayments}
                />
                <SidebarButton
                    urlLink={`/financial-documents`}
                    title={formatMessage(financialDocumentsMessages.financialDocuments)}
                    additionalActivePaths={[`/financial-documents/${financialDocumentId}`]}
                    hasPermission={permissionFinancialDocuments}
                />

                {showFinancialsSection && <SectionBlockSeparator className="my-2" />}

                {showBillingEntitiesSection && (
                    <SectionBlockTitle className="text-base">
                        {formatMessage(billingMessages.billingSettings)}
                    </SectionBlockTitle>
                )}

                <SidebarButton
                    urlLink={`/billing-entity`}
                    title={formatMessage(billingMessages.billingEntities)}
                    additionalActivePaths={[`/billing-entity/${entityId}`]}
                    hasPermission={permissionBillingEntities}
                />
                <SidebarButton
                    urlLink={`/money-accounts`}
                    title={formatMessage(moneyAccountsMessages.moneyAccounts)}
                    hasPermission={permissionMoneyAccounts}
                />
                <SidebarButton
                    urlLink={`/bank-accounts`}
                    title={formatMessage(bankAccountMessages.bankAccounts)}
                    hasPermission={permissionBillingEntities}
                />
                <SidebarButton
                    urlLink={`/accounting-headers`}
                    title={formatMessage(accountingHeadersMessages.accountingHeaders)}
                    hasPermission={permissionAccountingHeadings}
                />
                <SidebarButton
                    urlLink={`/accounting-ranges`}
                    title={formatMessage(accountingRangesMessages.accountingRanges)}
                    additionalActivePaths={[`/accounting-ranges/${rangeId}`]}
                    hasPermission={permissionAccountingRanges}
                />
                <SidebarButton
                    urlLink={`/units`}
                    title={formatMessage(unitsMessages.units)}
                    hasPermission={permissionUnits}
                />
                <SidebarButton
                    urlLink={`/tax-reasons`}
                    title={formatMessage(taxReasonsMessages.taxReasons)}
                    hasPermission={permissionTaxReasons}
                />

                <SectionBlockSeparator className="my-2" />
                <SectionBlockTitle className="text-base">
                    {formatMessage(webhookEndpointsMessages.systemSettings)}
                </SectionBlockTitle>
                <SidebarButton
                    urlLink={`/webhook-endpoints`}
                    title={formatMessage(webhookEndpointsMessages.webhookEndpoints)}
                    hasPermission={true}
                />

                <SectionBlockSeparator className="my-2" />
                <SectionBlockTitle className="text-base">
                    {formatMessage(billingRunsMessages.systemLogs)}
                </SectionBlockTitle>
                <SidebarButton
                    urlLink={`/billing-runs`}
                    title={formatMessage(billingRunsMessages.billingRuns)}
                    additionalActivePaths={[`/billing-runs/${billingRunId}`]}
                    hasPermission={permissionCharges}
                />
                <SidebarButton
                    urlLink={`/billing-attempts`}
                    title={formatMessage(billingAttemptsMessages.billingAttempts)}
                    hasPermission={permissionCharges}
                />
                <SidebarButton
                    urlLink={`/webhook-events`}
                    title={formatMessage(webhookEventsMessages.webhookEvents)}
                    hasPermission={true}
                />

                <SectionBlockSeparator className="my-2" />
                <SidebarButton
                    urlLink={`/profile`}
                    title={formatMessage(profileMessages.title)}
                    additionalActivePaths={[`/profile/password`, `/profile/settings`]}
                />
            </SectionBlock>
        </div>
    );
};

export default Sidebar;
